import { useState } from 'react';
import { CmmFile, CmmFileHeader } from '../../shared/types/cmmReport.ts';
import { sortByDesc } from '../../shared/utils/sortUtils.ts';
import { FileSource } from '../services/cmm-import/types.ts';
import { fetchCmmHeaderFiles, fetchCmmReports } from '../services/fetchCmmReports.ts';
import { trpcClient } from '../utils/trpc/trpc.ts';


interface FilesDialogState {
    cmmReports: CmmFileHeader[];
    initialFilter: string;
}


interface Props {
    partName: string;
    orderNumber?: string;
    partCount?: null | number;
    onFilesSelected: (valueSource: FileSource, cmmReports: CmmFile[]) => void;
    attemptApplyAutoImport?: (valueSource: FileSource, cmmReport: CmmFile) => Promise<boolean>;
}

export function useCmmFileSelect({
    partName,
    orderNumber,
    partCount,
    onFilesSelected,
    attemptApplyAutoImport,
}: Props) {
    const [filesDialogState, setFilesDialogState] = useState<FilesDialogState | null>(null);
    const [isAutoImportClicked, setIsAutoImportClicked] = useState(false);
    const [isManualImportClicked, setIsManualImportClicked] = useState(false);


    const handleAutoImportClick = async () => {
        if (isAutoImportClicked) return;
        try {
            setIsAutoImportClicked(true);
            if (!orderNumber) {
                alert('Record must have order number to import automatically');
                return;
            }

            const cmmReports = await fetchCmmReports(orderNumber);
            if (cmmReports.length === 0) {
                alert(`No CMM reports found (looking for files in sharepoint that begin with "${orderNumber}")`);
                return;
            }

            const filteredByPartCount = partCount != null
                ? cmmReports.filter(it => containsToken(it.name, partCount!.toString()))
                : cmmReports;
            const reportsFinal = filteredByPartCount.length > 0 ? filteredByPartCount : cmmReports;
            sortByDesc(reportsFinal, it => it.lastModified);

            const importedWithoutDialog = await attemptImportWithoutDialog(reportsFinal);
            if (!importedWithoutDialog) {
                onFilesSelected('sharepoint', reportsFinal);
            }
        } catch (error) {
            alert('Failed to fetch CMM reports');
        } finally {
            setIsAutoImportClicked(false);
        }
    }

    const attemptImportWithoutDialog = async (cmmReports: CmmFile[]) => {
        if (cmmReports.length !== 1) {
            return false;
        }

        const partCountMatches = partCount != null && containsToken(cmmReports[0].name, partCount.toString());
        if (!partCountMatches) {
            return false;
        }

        const result = await attemptApplyAutoImport?.('sharepoint', cmmReports[0]);
        return result ?? false;
    }

    const handleManualImportClick = async () => {
        if (isAutoImportClicked || isManualImportClicked) return;
        try {
            setIsManualImportClicked(true);
            const cmmReportsHeaders = await trpcClient.cmmReports.getHeaders.query({});
            sortByDesc(cmmReportsHeaders, it => it.lastModified);
            setFilesDialogState({
                cmmReports: cmmReportsHeaders,
                initialFilter: partName,
            });
        } catch (error) {
            alert('Failed to fetch CMM reports');
        } finally {
            setIsManualImportClicked(false);
        }
    }

    const containsToken = (str: string, token: string) => {
        // Token must be surrounded by non-word characters, an underscore or start/end of string
        const regex = new RegExp(`(\\W|_|^)${token}(\\W|_|$)`, 'i');
        return regex.test(str);
    }

    const handleFilesDialogSubmit = async (cmmReports: CmmFileHeader[]) => {
        try {
            const reportsWithFiles = await fetchCmmHeaderFiles(cmmReports);
            onFilesSelected('sharepoint', reportsWithFiles);
            setFilesDialogState(null);
        } catch (error) {
            alert('Failed to fetch CMM reports');
        }
    };

    const handleLocalFilesSelected = async (files: File[]) => {
        const cmmReports = files.map(file => ({
            name: file.name,
            lastModified: file.lastModified ? new Date(file.lastModified) : new Date(),
            file,
        }));

        onFilesSelected('local', cmmReports);
    };

    return {
        isAutoImportClicked,
        isManualImportClicked,
        filesDialogState,
        handleAutoImportClick,
        handleManualImportClick,
        handleFilesDialogSubmit,
        handleFilesDialogCancel: () => setFilesDialogState(null),
        handleLocalFilesSelected,
    };
}
